import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { notifications } from '@mantine/notifications'
import { useUsuarioCreate, type UsuarioCreateInput } from '../../hooks/useUsuarios'
import { extractApiError } from '../../lib/extractApiError'
import styles from '../_shared/CrudPage.module.css'
import formStyles from '../_shared/CrudForm.module.css'

const PERFIS = [
  { value: 'rt_mecanico', label: 'RT Mecânico (assina PMOC mecânico)' },
  { value: 'rt_qai', label: 'RT QAI (assina laudos QAI)' },
  { value: 'executora', label: 'Técnico / Executora' },
  { value: 'admin_tenant', label: 'Administrador do Tenant' },
  { value: 'cliente', label: 'Cliente / Contratante' },
]

export default function UsuarioNovoPage() {
  const navigate = useNavigate()
  const create = useUsuarioCreate()
  const [form, setForm] = useState<UsuarioCreateInput>({
    nome_completo: '',
    email: '',
    perfil: 'executora',
    senha: '',
    registro_profissional: '',
  })

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (form.senha.length < 8) {
      notifications.show({ color: 'red', message: 'A senha deve ter ao menos 8 caracteres.' })
      return
    }
    if ((form.perfil === 'rt_mecanico' || form.perfil === 'rt_qai') && !form.registro_profissional) {
      notifications.show({
        color: 'red',
        message: 'RT exige registro profissional (CREA, CRQ, CFF, etc).',
      })
      return
    }
    try {
      await create.mutateAsync(form)
      notifications.show({
        color: 'green',
        message: `Usuário ${form.email} criado. Senha foi enviada para o administrador.`,
      })
      navigate('/usuarios')
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Falha',
        message: extractApiError(err, 'Erro ao criar usuário.'),
      })
    }
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <button
            className={styles.backBtn}
            type="button"
            onClick={() => navigate('/usuarios')}
          >
            ← Voltar
          </button>
          <h1 className={styles.title}>Novo Usuário</h1>
        </div>
      </header>

      <form onSubmit={handleSubmit} className={formStyles.form}>
        <section className={formStyles.section}>
          <h2 className={formStyles.sectionTitle}>Dados</h2>
          <div className={formStyles.grid2}>
            <div className={`${formStyles.field} ${formStyles.span2}`}>
              <label htmlFor="nome">Nome completo *</label>
              <input
                id="nome"
                data-testid="usuario-nome"
                value={form.nome_completo}
                onChange={(e) => setForm({ ...form, nome_completo: e.target.value })}
                required
              />
            </div>

            <div className={formStyles.field}>
              <label htmlFor="email">E-mail *</label>
              <input
                id="email"
                type="email"
                data-testid="usuario-email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
              />
              <small style={{ color: '#666' }}>Usado para login.</small>
            </div>

            <div className={formStyles.field}>
              <label htmlFor="perfil">Perfil *</label>
              <select
                id="perfil"
                data-testid="usuario-perfil"
                value={form.perfil}
                onChange={(e) => setForm({ ...form, perfil: e.target.value })}
                required
              >
                {PERFIS.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>

            {(form.perfil === 'rt_mecanico' || form.perfil === 'rt_qai') && (
              <div className={`${formStyles.field} ${formStyles.span2}`}>
                <label htmlFor="reg">Registro profissional *</label>
                <input
                  id="reg"
                  data-testid="usuario-registro"
                  value={form.registro_profissional ?? ''}
                  onChange={(e) =>
                    setForm({ ...form, registro_profissional: e.target.value })
                  }
                  placeholder="ex.: CREA-SP 5061701055"
                  required
                />
              </div>
            )}

            <div className={`${formStyles.field} ${formStyles.span2}`}>
              <label htmlFor="senha">Senha provisória * (mín. 8 caracteres)</label>
              <input
                id="senha"
                type="password"
                data-testid="usuario-senha"
                value={form.senha}
                onChange={(e) => setForm({ ...form, senha: e.target.value })}
                required
                minLength={8}
              />
              <small style={{ color: '#666' }}>
                Compartilhe esta senha de forma segura. O usuário deverá trocá-la no
                primeiro acesso (em breve — fluxo automatizado planejado).
              </small>
            </div>
          </div>
        </section>

        <div className={formStyles.actions}>
          <button
            type="submit"
            className={formStyles.btnPrimary}
            disabled={create.isPending}
            data-testid="usuario-submit"
          >
            {create.isPending ? 'Criando...' : 'Criar usuário'}
          </button>
        </div>
      </form>
    </div>
  )
}
